import { SalesBudgetView } from "@/components/blocks/budget";
import { RevisionDetailsView } from "@/components/blocks/budget/revision-details-view";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import type { BudgetRevision } from "@/types/budget.types";
import { useProjectDetailsContext } from "../context";

export function SalesBudgetTab() {
	const ctx = useProjectDetailsContext();
	const {
		project,
		mockSeedProjectId,
		budgetData,
		enrichedBudgetData,
		budgetLoading,
		budgetError,
		fixedFeeInvoices,
		fixedFeeInvoicedToDate,
		setEditSalesBudgetPageOpen,
		setBudgetExportModalOpen,
		setActiveBudgetType,
		showSalesRevisionHistory,
		setShowSalesRevisionHistory,
		salesRevisions,
		setSalesLoadedRevision,
		setSalesMakeActiveRevision,
		setSalesMakeActiveSource,
		setIsSalesNewRevision,
		effectiveCompletionPercent,
	} = ctx;

	// The Cost Centre roll-up is applied centrally in useProjectDetails so this
	// tab, Billing, Overview and Financials all report the same revenue budget.
	const salesBudgetData = enrichedBudgetData?.salesBudget;

	if (!project) return null;

	return (
		<>
			{budgetLoading ? (
				<div className="flex items-center justify-center h-96">
					<div className="text-center">
						<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
						<p className="text-muted-foreground">Loading budget data...</p>
					</div>
				</div>
			) : budgetError ? (
				<div className="flex items-center justify-center h-96">
					<div className="text-center space-y-4">
						<div className="text-destructive mb-2">
							Error loading budget data
						</div>
						<p className="text-sm text-muted-foreground">{budgetError}</p>
					</div>
				</div>
			) : (
				<SalesBudgetView
					projectId={mockSeedProjectId}
					data={salesBudgetData}
					costBudget={budgetData?.costBudget}
					projectType={project.type}
					billingTriggerType={project.billingTriggerType}
					completionPercent={
						project.type === "fixed-price"
							? effectiveCompletionPercent
							: undefined
					}
					completionPercentMode={
						project.type === "fixed-price"
							? (project.completionPercentMode ?? "based-on-time")
							: undefined
					}
					completionPercentOverriddenBy={
						project.type === "fixed-price"
							? project.completionPercentOverriddenBy
							: undefined
					}
					invoicedToDate={
						project.type === "fixed-price" ? fixedFeeInvoicedToDate : undefined
					}
					milestoneInvoices={
						project.type === "fixed-price" ? fixedFeeInvoices : undefined
					}
					onCreateBudget={() => {
						setIsSalesNewRevision(false);
						setEditSalesBudgetPageOpen(true);
					}}
					onEdit={() => {
						setIsSalesNewRevision(false);
						setEditSalesBudgetPageOpen(true);
					}}
					onExport={() => {
						setActiveBudgetType("sales");
						setBudgetExportModalOpen(true);
					}}
					// Revision History hidden for first release — omit onHistory so the
					// button no longer renders. Sheet/state/handlers kept for re-enablement.
					// onHistory={() => {
					// 	setShowSalesRevisionHistory(true);
					// }}
				/>
			)}

			{/* Sales Budget Revision History Sheet — rendered outside the
          loading/error/data conditional so it stays mounted across
          budget refetch cycles and Radix Dialog state is preserved. */}
			<Sheet
				open={showSalesRevisionHistory}
				onOpenChange={setShowSalesRevisionHistory}
			>
				<SheetContent
					side="right"
					className="w-full sm:max-w-4xl overflow-y-auto"
				>
					<SheetHeader className="px-6 pt-6">
						<SheetTitle>Sales Budget Revision History</SheetTitle>
						<SheetDescription>
							View, compare and restore previous versions of the sales budget.
						</SheetDescription>
					</SheetHeader>
					<div className="mt-6 px-6 pb-6">
						<RevisionDetailsView
							budgetLayer="sales"
							revisions={salesRevisions}
							projectName={project?.name ?? ""}
							onViewRevision={(id) => {
								const rev = salesRevisions.find(
									(r: BudgetRevision) =>
										r.id === Number(id) || r.revisionNumber === Number(id),
								);
								if (rev) {
									setSalesLoadedRevision(rev);
									setIsSalesNewRevision(false);
									setEditSalesBudgetPageOpen(true);
								}
								setShowSalesRevisionHistory(false);
							}}
							onRestore={(id) => {
								const rev = salesRevisions.find(
									(r: BudgetRevision) => r.id === Number(id),
								);
								if (rev) {
									setSalesMakeActiveSource("restore");
									setSalesMakeActiveRevision(rev);
								}
							}}
							onMakeActive={(id) => {
								const rev = salesRevisions.find(
									(r: BudgetRevision) => r.id === Number(id),
								);
								if (rev) {
									setSalesMakeActiveSource("make-active");
									setSalesMakeActiveRevision(rev);
								}
							}}
							onNewRevision={() => {
								setShowSalesRevisionHistory(false);
								setSalesLoadedRevision(null);
								setIsSalesNewRevision(true);
								setEditSalesBudgetPageOpen(true);
							}}
							onDuplicate={(id) => {
								const rev = salesRevisions.find(
									(r: BudgetRevision) =>
										r.id === Number(id) || r.revisionNumber === Number(id),
								);
								if (rev) {
									setShowSalesRevisionHistory(false);
									setSalesLoadedRevision(rev);
									setIsSalesNewRevision(false);
									setEditSalesBudgetPageOpen(true);
								}
							}}
						/>
					</div>
				</SheetContent>
			</Sheet>
		</>
	);
}
